/**
 * useSessionLifecycle — DO-only session lifecycle for project chat.
 *
 * All messages flow through a single source: the Durable Object WebSocket.
 * Prompts are sent via the REST API (POST /sessions/:sessionId/prompt).
 * Agent state (idle/prompting/responding) is derived from message flow.
 */
import type { NodeResponse, WorkspaceResponse } from '@simple-agent-manager/shared';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { ChatConnectionState } from '../../hooks/useChatWebSocket';
import { useChatWebSocket } from '../../hooks/useChatWebSocket';
import { useTokenRefresh } from '../../hooks/useTokenRefresh';
import { useWorkspacePorts } from '../../hooks/useWorkspacePorts';
import type { ChatMessageResponse, ChatSessionDetailResponse, ChatSessionResponse, SessionStateSnapshot } from '../../lib/api';
import { cancelAgentPrompt, getChatSession, getNode, getTerminalToken, getTranscribeApiUrl, getWorkspace, resetIdleTimer, sendFollowUpPrompt, uploadSessionFiles } from '../../lib/api';
import { mergeMessages } from '../../lib/merge-messages';
import { isWorkspaceOperational } from '../../lib/workspace-status-utils';
import type { SessionState } from './types';
import { deriveSessionState, IDLE_TIMEOUT_MS, VIRTUAL_START } from './types';
import { useConnectionRecovery } from './useConnectionRecovery';

/** Agent activity state derived from message flow (no ACP connection needed). */
export type AgentActivityState = 'idle' | 'prompting' | 'responding';

export interface UseSessionLifecycleResult {
  // Session state
  session: ChatSessionResponse | null;
  messages: ChatMessageResponse[];
  hasMore: boolean;
  loading: boolean;
  error: string | null;
  setError: (e: string | null) => void;
  sessionState: SessionState;

  // Task embed
  taskEmbed: ChatSessionResponse['task'] | null;

  // Workspace context
  workspace: WorkspaceResponse | null;
  node: NodeResponse | null;
  detectedPorts: ReturnType<typeof useWorkspacePorts>['ports'];

  // Follow-up state
  followUp: string;
  setFollowUp: (v: string) => void;
  sendingFollowUp: boolean;
  uploading: boolean;

  // Resume state
  isResuming: boolean;
  resumeError: string | null;

  // Connection state
  connectionState: ChatConnectionState;
  showConnectionBanner: boolean;
  retryWs: () => void;

  // Agent activity (derived from message flow + server state)
  agentActivity: AgentActivityState;
  currentPlan: SessionStateSnapshot['currentPlan'];
  promptStartedAt: number | null;

  // Scroll state
  firstItemIndex: number;
  showScrollButton: boolean;
  setShowScrollButton: (v: boolean) => void;

  // Idle timer
  idleCountdownMs: number | null;

  // File panel
  filePanel: { mode: 'browse' | 'view' | 'diff' | 'git-status'; path?: string; line?: number | null } | null;
  setFilePanel: (v: { mode: 'browse' | 'view' | 'diff' | 'git-status'; path?: string; line?: number | null } | null) => void;
  handleFileClick: (path: string, line?: number | null) => void;
  handleOpenFileBrowser: () => void;
  handleOpenGitChanges: () => void;

  // Actions
  handleCancelPrompt: () => void;
  handleSendFollowUp: () => Promise<void>;
  handleUploadFiles: (files: FileList | File[]) => Promise<void>;
  loadMore: () => Promise<void>;
  loadingMore: boolean;

  // Misc
  transcribeApiUrl: string;
  wsRef: React.RefObject<WebSocket | null>;
}

export function useSessionLifecycle(
  projectId: string,
  sessionId: string,
  isProvisioning: boolean,
  _onSessionMutated?: () => void,
): UseSessionLifecycleResult {
  const [session, setSession] = useState<ChatSessionResponse | null>(null);
  const [taskEmbed, setTaskEmbed] = useState<ChatSessionResponse['task'] | null>(null);
  const [messages, setMessages] = useState<ChatMessageResponse[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Workspace & node context
  const [workspace, setWorkspace] = useState<WorkspaceResponse | null>(null);
  const [node, setNode] = useState<NodeResponse | null>(null);

  // Follow-up input state
  const [followUp, setFollowUp] = useState('');
  const [sendingFollowUp, setSendingFollowUp] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Agent activity state (derived from message flow + server state)
  const [agentActivity, setAgentActivity] = useState<AgentActivityState>('idle');
  const idleTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Plan state (from session state mirror)
  const [currentPlan, setCurrentPlan] = useState<SessionStateSnapshot['currentPlan']>(null);
  const [promptStartedAt, setPromptStartedAt] = useState<number | null>(null);

  // File panel
  const [filePanel, setFilePanel] = useState<{
    mode: 'browse' | 'view' | 'diff' | 'git-status';
    path?: string;
    line?: number | null;
  } | null>(null);

  const handleFileClick = useCallback((path: string, line?: number | null) => {
    setFilePanel({ mode: 'view', path, line });
  }, []);
  const handleOpenFileBrowser = useCallback(() => {
    setFilePanel({ mode: 'browse', path: '.' });
  }, []);
  const handleOpenGitChanges = useCallback(() => {
    setFilePanel({ mode: 'git-status' });
  }, []);

  // Virtual scroll
  const [firstItemIndex, setFirstItemIndex] = useState(VIRTUAL_START);
  const [showScrollButton, setShowScrollButton] = useState(false);

  const sessionState = session ? deriveSessionState(session) : 'terminated';
  const transcribeApiUrl = getTranscribeApiUrl();

  // ── DO WebSocket (sole message source) ──
  const { connectionState, wsRef, retry: retryWs } = useChatWebSocket({
    projectId,
    sessionId,
    enabled: session?.status === 'active',
    onMessage: useCallback((msg: ChatMessageResponse) => {
      setMessages((prev) => mergeMessages(prev, [msg], 'append'));

      // Update current plan from incoming plan messages
      if (msg.role === 'plan' && msg.content) {
        try {
          const parsed = JSON.parse(msg.content);
          if (Array.isArray(parsed)) setCurrentPlan(parsed);
        } catch { /* ignore malformed plan */ }
      }

      // Derive agent activity from any non-user message (assistant, tool, thinking, plan).
      // Reset the idle fallback timer on every message role — tool calls and
      // thinking chunks also indicate the agent is active. The 30s timeout is a
      // safety net; the primary signal is the server-pushed session.activity event.
      if (msg.role !== 'user') {
        setAgentActivity('responding');
        clearTimeout(idleTimerRef.current);
        idleTimerRef.current = setTimeout(() => {
          setAgentActivity('idle');
        }, IDLE_TIMEOUT_MS);
      }
    }, []),
    onSessionStopped: useCallback(() => {
      setSession((prev) => prev ? { ...prev, status: 'stopped' } : prev);
      setAgentActivity('idle');
    }, []),
    onCatchUp: useCallback((catchUpMessages: ChatMessageResponse[], catchUpSession: ChatSessionResponse, state?: SessionStateSnapshot | null) => {
      setSession(catchUpSession);
      setMessages((prev) => mergeMessages(prev, catchUpMessages, 'replace'));
      // Hydrate activity + plan from server state on reconnect
      if (state) {
        if (state.activity === 'prompting') {
          setAgentActivity('prompting');
          setPromptStartedAt(state.promptStartedAt ?? null);
        } else if (state.activity === 'idle') {
          setAgentActivity('idle');
        }
        if (state.currentPlan) setCurrentPlan(state.currentPlan);
      }
    }, []),
    onAgentCompleted: useCallback((agentCompletedAt: number) => {
      setSession((prev) => prev ? { ...prev, agentCompletedAt, isIdle: true } as ChatSessionResponse : prev);
      setAgentActivity('idle');
    }, []),
    onAgentActivity: useCallback((activity: 'prompting' | 'idle', promptStartedAt?: number | null) => {
      setAgentActivity(activity === 'prompting' ? 'prompting' : 'idle');
      setPromptStartedAt(activity === 'prompting' ? (promptStartedAt ?? Date.now()) : null);
      clearTimeout(idleTimerRef.current);
      if (activity === 'prompting') {
        // Safety backstop: if the server never sends "idle" (e.g., crashed or
        // network partition), fall back to idle after the same timeout used by
        // the message-based heuristic.
        idleTimerRef.current = setTimeout(() => {
          setAgentActivity('idle');
        }, IDLE_TIMEOUT_MS);
      }
    }, []),
  });

  // Connection recovery (banner debounce, idle timer, auto-resume)
  const recovery = useConnectionRecovery({
    sessionId,
    projectId,
    sessionState,
    connectionState,
    session,
    isProvisioning,
    setSession,
  });

  // Reset virtual scroll on session change
  useEffect(() => {
    setFirstItemIndex(VIRTUAL_START);
    setShowScrollButton(false);
  }, [sessionId]);

  // Cleanup idle timer on unmount
  useEffect(() => {
    return () => clearTimeout(idleTimerRef.current);
  }, []);

  // Hydrate session state from server snapshot
  const hydrateSessionState = useCallback((state: SessionStateSnapshot | null | undefined) => {
    if (!state) return;
    if (state.activity === 'prompting') {
      setAgentActivity('prompting');
      setPromptStartedAt(state.promptStartedAt ?? null);
    } else if (state.activity === 'idle') {
      setAgentActivity('idle');
    }
    if (state.currentPlan) {
      setCurrentPlan(state.currentPlan);
    }
  }, []);

  // Load session
  const loadSession = useCallback(async () => {
    try {
      setError(null);
      setLoading(true);
      const data: ChatSessionDetailResponse = await getChatSession(projectId, sessionId);
      setSession(data.session);
      setMessages(data.messages);
      setHasMore(data.hasMore);
      if (data.session.task) setTaskEmbed(data.session.task);
      // Hydrate activity + plan from persisted session state
      hydrateSessionState(data.state);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load session');
    } finally {
      setLoading(false);
    }
  }, [projectId, sessionId, hydrateSessionState]);

  useEffect(() => { void loadSession(); }, [loadSession]);

  // Fetch workspace and node details
  useEffect(() => {
    const wsId = session?.workspaceId;
    if (!wsId) return;
    if (workspace?.id === wsId) return;

    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    const RETRY_DELAYS_MS = [2_000, 5_000, 10_000];

    async function attemptFetch(attempt = 0) {
      try {
        const ws = await getWorkspace(wsId!);
        if (cancelled) return;
        setWorkspace(ws);
        if (ws.nodeId) {
          const nd = await getNode(ws.nodeId);
          if (!cancelled) setNode(nd);
        }
      } catch {
        if (cancelled) return;
        if (attempt < RETRY_DELAYS_MS.length) {
          retryTimer = setTimeout(() => attemptFetch(attempt + 1), RETRY_DELAYS_MS[attempt]);
        }
      }
    }

    attemptFetch();
    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [session?.workspaceId, workspace?.id]);

  // Token refresh for port scanning
  const isWorkspaceRunning = isWorkspaceOperational(workspace?.status);
  const tokenRefreshFetchToken = useCallback(async () => {
    const wsId = session?.workspaceId;
    if (!wsId) throw new Error('No workspace ID');
    return getTerminalToken(wsId);
  }, [session?.workspaceId]);

  const { token: terminalToken } = useTokenRefresh({
    fetchToken: tokenRefreshFetchToken,
    enabled: !!session?.workspaceId && isWorkspaceRunning,
  });

  const { ports: detectedPorts } = useWorkspacePorts(
    workspace?.url ?? undefined,
    session?.workspaceId ?? undefined,
    terminalToken ?? undefined,
    isWorkspaceRunning,
  );

  // Polling fallback
  useEffect(() => {
    if (!session || session.status !== 'active') return;

    const abortController = new AbortController();
    const ACTIVE_POLL_MS = 3000;
    let lastPollFingerprint = '';
    const pollInterval = setInterval(async () => {
      try {
        const data: ChatSessionDetailResponse = await getChatSession(
          projectId, sessionId, { signal: abortController.signal },
        );
        if (data.session.id !== sessionId) return;
        const newLastId = data.messages[data.messages.length - 1]?.id ?? '';
        const taskStatus = data.session.task?.status ?? '';
        const agentSessId = data.session.agentSessionId ?? '';
        const fingerprint = `${data.messages.length}:${newLastId}:${data.session.status}:${taskStatus}:${agentSessId}`;
        if (fingerprint !== lastPollFingerprint) {
          lastPollFingerprint = fingerprint;
          setSession(data.session);
          setMessages((prev) => mergeMessages(prev, data.messages, 'replace'));
          if (data.session.task) setTaskEmbed(data.session.task);
        }
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
      }
    }, ACTIVE_POLL_MS);

    return () => {
      clearInterval(pollInterval);
      abortController.abort();
    };
  }, [session?.status, projectId, sessionId]);

  // ── Send follow-up via REST API ──
  const handleSendFollowUp = async () => {
    const trimmed = followUp.trim();
    if (!trimmed || sendingFollowUp) return;

    setSendingFollowUp(true);
    setAgentActivity('prompting');
    try {
      if (sessionState === 'idle') {
        resetIdleTimer(projectId, sessionId)
          .then((result) => {
            if (result.cleanupAt) {
              setSession((prev) => {
                if (!prev) return prev;
                return { ...prev, cleanupAt: result.cleanupAt, isIdle: false, agentCompletedAt: null } as ChatSessionResponse;
              });
            }
          })
          .catch(() => {});
      }

      // Optimistic user message
      const optimisticId = `optimistic-${crypto.randomUUID()}`;
      setMessages((prev) => [...prev, {
        id: optimisticId,
        sessionId,
        role: 'user',
        content: trimmed,
        toolMetadata: null,
        createdAt: Date.now(),
      }]);

      // Persist via DO WebSocket
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'message.send',
          sessionId,
          content: trimmed,
          role: 'user',
        }));
      }

      // For idle sessions, resume first then send the prompt
      if (sessionState === 'idle' && session?.workspaceId && session?.agentSessionId) {
        recovery.resumeAndSend(trimmed);
      } else {
        // Forward prompt to the running agent via REST API
        try {
          await sendFollowUpPrompt(projectId, sessionId, trimmed);
        } catch {
          // Agent may be offline — message is still persisted via DO.
          setAgentActivity('idle');
        }
      }

      setFollowUp('');
    } finally {
      setSendingFollowUp(false);
    }
  };

  // Upload files
  const handleUploadFiles = useCallback(async (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    if (fileArray.length === 0) return;
    setUploading(true);
    try {
      const result = await uploadSessionFiles(projectId, sessionId, fileArray);
      const names = result.files.map((f) => f.name).join(', ');
      setMessages((prev) => [...prev, {
        id: `optimistic-upload-${crypto.randomUUID()}`,
        sessionId,
        role: 'user' as const,
        content: `Uploaded ${result.files.length} file${result.files.length > 1 ? 's' : ''}: ${names}`,
        toolMetadata: null,
        createdAt: Date.now(),
      }]);
    } catch (err) {
      console.error('File upload failed:', err);
    } finally {
      setUploading(false);
    }
  }, [projectId, sessionId]);

  // Cancel the current in-flight prompt via REST API
  const cancellingRef = useRef(false);
  const handleCancelPrompt = useCallback(() => {
    if (agentActivity === 'idle' || cancellingRef.current) return;
    cancellingRef.current = true;
    cancelAgentPrompt(projectId, sessionId)
      .then(() => {
        setAgentActivity('idle');
      })
      .catch(() => {
        // Network/server error — keep spinner visible so user can retry
      })
      .finally(() => {
        cancellingRef.current = false;
      });
  }, [agentActivity, projectId, sessionId]);

  // Load more (pagination)
  const loadMore = async () => {
    if (!hasMore || loadingMore) return;
    const firstMessage = messages[0];
    if (!firstMessage) return;

    setLoadingMore(true);
    try {
      const data = await getChatSession(projectId, sessionId, {
        before: firstMessage.createdAt,
      });
      setMessages((prev) => {
        const merged = mergeMessages(prev, data.messages, 'prepend');
        const actualAdded = merged.length - prev.length;
        setFirstItemIndex((fi) => fi - actualAdded);
        return merged;
      });
      setHasMore(data.hasMore);
    } finally {
      setLoadingMore(false);
    }
  };

  return {
    session,
    messages,
    hasMore,
    loading,
    error,
    setError,
    sessionState,
    taskEmbed,
    workspace,
    node,
    detectedPorts,
    followUp,
    setFollowUp,
    sendingFollowUp,
    uploading,
    isResuming: recovery.isResuming,
    resumeError: recovery.resumeError,
    connectionState,
    showConnectionBanner: recovery.showConnectionBanner,
    retryWs,
    agentActivity,
    currentPlan,
    promptStartedAt,
    firstItemIndex,
    showScrollButton,
    setShowScrollButton,
    idleCountdownMs: recovery.idleCountdownMs,
    filePanel,
    setFilePanel,
    handleFileClick,
    handleOpenFileBrowser,
    handleOpenGitChanges,
    handleCancelPrompt,
    handleSendFollowUp,
    handleUploadFiles,
    loadMore,
    loadingMore,
    transcribeApiUrl,
    wsRef,
  };
}
